import { CHART_INDICATORS_STORAGE_KEYS } from "@/lib/localStorageKeys/chart-indicators";
import { ChartIndicators } from "./types";

export const getStoredIndicators = (): ChartIndicators | null => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const storedData = localStorage.getItem(CHART_INDICATORS_STORAGE_KEYS.CORE);

    if (!storedData) {
      return null;
    }

    return JSON.parse(storedData);
  } catch (error) {
    console.error("Error loading chart indicators from localStorage:", error);
    return null;
  }
};

export const saveIndicators = (indicators: ChartIndicators): void => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    localStorage.setItem(
      CHART_INDICATORS_STORAGE_KEYS.CORE,
      JSON.stringify(indicators)
    );
  } catch (error) {
    console.error("Error saving chart indicators to localStorage:", error);
  }
};

let indicatorsStorageListenerAttached = false;

/**
 * Subscribes to cross-window `storage` events for the indicators key so that
 * changes made in one window (main app or chart popup) are reflected in all
 * other windows. The `storage` event only fires in windows OTHER than the one
 * that performed the write, and `onChange` should only read (not save), so no
 * feedback loop is possible. Idempotent: attaches at most one listener.
 */
export const subscribeToIndicatorsStorageChanges = (
  onChange: () => void
): void => {
  if (typeof window === "undefined" || indicatorsStorageListenerAttached) {
    return;
  }

  indicatorsStorageListenerAttached = true;

  window.addEventListener("storage", (event: StorageEvent) => {
    if (event.key === null || event.key === CHART_INDICATORS_STORAGE_KEYS.CORE) {
      onChange();
    }
  });
};

/**
 * Get the size of stored chart indicators in bytes
 * @returns Size in bytes or 0 if not found
 */
export function getStoredChartIndicatorsValue(): number {
  if (typeof window === "undefined") return 0;

  try {
    const stored = localStorage.getItem(CHART_INDICATORS_STORAGE_KEYS.CORE);

    if (!stored) return 0;

    // Calculate size in bytes (JavaScript strings are UTF-16, so each character is 2 bytes)
    return stored.length * 2;
  } catch (error) {
    console.error("Error getting chart indicators size:", error);
    return 0;
  }
}

/**
 * Clear stored chart indicators
 * @returns Success status
 */
export function clearStoredChartIndicators(): boolean {
  if (typeof window === "undefined") return false;

  try {
    localStorage.removeItem(CHART_INDICATORS_STORAGE_KEYS.CORE);
    return true;
  } catch (error) {
    console.error("Error clearing chart indicators:", error);
    return false;
  }
}
